import type {
  FormDescriptor,
  FormMode,
  FormSubmissionResult,
  ResolvedApiBinding,
  ResolvedExperienceShell,
  ResolvedResourceExperienceDescriptor,
  ResolvedRouteExperienceDescriptor,
  TableDescriptor,
  TableQueryFilter,
} from '@finance/contracts';
import { ApiError, httpClient, parseApiError } from '../../api/http-client';

interface ApiDataEnvelope<T> {
  data?: T;
}

export interface ParsedImportCsv {
  headers: string[];
  rows: Record<string, string>[];
}

function parseCsvRecord(line: string): string[] {
  const cells: string[] = [];
  let value = '';
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index]!;
    if (character === '"') {
      if (quoted && line[index + 1] === '"') {
        value += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === ',' && !quoted) {
      cells.push(value.trim());
      value = '';
    } else {
      value += character;
    }
  }
  if (quoted) throw new Error('The CSV file contains an unclosed quoted value.');
  cells.push(value.trim());
  return cells;
}

/** Converts an operator-selected CSV file into the API's normalized import payload. */
export function parseImportCsv(content: string): ParsedImportCsv {
  const lines = content.replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length < 2) throw new Error('The import file must include a header row and at least one data row.');
  const headers = parseCsvRecord(lines[0]!);
  if (headers.some((header) => !header)) throw new Error('The import file contains an empty column header.');
  if (new Set(headers).size !== headers.length) throw new Error('The import file contains duplicate column headers.');
  return {
    headers,
    rows: lines.slice(1).map((line, index) => {
      const cells = parseCsvRecord(line);
      if (cells.length !== headers.length) {
        throw new Error(`CSV row ${index + 2} has ${cells.length} values; expected ${headers.length}.`);
      }
      return Object.fromEntries(headers.map((header, cellIndex) => [header, cells[cellIndex] ?? '']));
    }),
  };
}

async function prepareImportPayload(values: Record<string, unknown>): Promise<Record<string, unknown>> {
  const file = values.file;
  if (!(typeof File !== 'undefined' && file instanceof File)) {
    throw new Error('Select a CSV file before uploading an import.');
  }
  if (!/\.csv$/i.test(file.name)) throw new Error('Only CSV import files are currently supported.');
  if (file.size === 0 || file.size > 10 * 1024 * 1024) {
    throw new Error('The import file must be between 1 byte and 10 MB.');
  }
  const parsed = parseImportCsv(await file.text());
  return {
    importType: values.importType,
    sourceFileName: file.name,
    ...(typeof values.sourceSheetName === 'string' && values.sourceSheetName.trim()
      ? { sourceSheetName: values.sourceSheetName.trim() }
      : {}),
    headers: parsed.headers,
    rows: parsed.rows,
  };
}

export interface GenericResourceListQuery {
  readonly page?: number;
  readonly pageSize?: number;
  readonly sortField?: string;
  readonly sortDirection?: 'asc' | 'desc';
  readonly search?: string;
  /** Canonical operator-aware filters validated against the resolved table descriptor. */
  readonly filters?: readonly TableQueryFilter[];
  /** Descriptor-validated server grouping; never inferred from browser rows. */
  readonly grouping?: readonly { readonly field: string; readonly direction: 'asc' | 'desc' }[];
  /** Descriptor-validated scope-wide aggregates. */
  readonly aggregations?: readonly { readonly field: string; readonly function: string }[];
}

export interface GenericResourceListResponse<
  TItem extends Record<string, unknown> = Record<string, unknown>,
> {
  readonly items: readonly TItem[];
  readonly pageInfo?: {
    readonly page?: number;
    readonly pageSize?: number;
    readonly total?: number;
    readonly hasNextPage?: boolean;
    readonly endCursor?: string;
  };
  readonly summary?: Readonly<Record<string, unknown>>;
  readonly aggregates?: Readonly<Record<string, unknown>>;
  readonly groupMetadata?: Readonly<Record<string, unknown>>;
  readonly availableActions?: readonly string[];
}

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) throw await parseApiError(response);
  if (response.status === 204) return undefined as T;
  const body = (await response.json()) as T | ApiDataEnvelope<T>;
  if (
    body &&
    typeof body === 'object' &&
    'data' in body &&
    (body as ApiDataEnvelope<T>).data !== undefined
  ) {
    return (body as ApiDataEnvelope<T>).data as T;
  }
  return body as T;
}

async function getExperienceJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  return parseJson<T>(await httpClient(path, { method: 'GET', signal }));
}

function resolveBindingPath(
  binding: ResolvedApiBinding,
  params: Readonly<Record<string, string>> = {}
): string {
  let path = binding.path;
  for (const [name, value] of Object.entries(params)) {
    path = path.replaceAll(`{${name}}`, encodeURIComponent(value));
  }
  if (/\{[^}]+\}/.test(path)) {
    throw new Error(`Resolved API binding ${binding.operationId} has missing path parameters`);
  }
  return path;
}

function mutationIdempotencyKey(binding: ResolvedApiBinding): string | undefined {
  return binding.idempotencyRequired ? crypto.randomUUID() : undefined;
}

function mutationHeaders(idempotencyKey?: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
  };
}

/**
 * P0-005: Authorization fingerprint for experience query keys.
 *
 * Experience descriptors, tables, forms, and resource data are all resolved
 * server-side against the authenticated actor's persona, scope, AAL,
 * permissions version, and entitlements version. A query cached for one
 * authorization context is invalid for another — even within the same session.
 *
 * Every experience query key includes this fingerprint so that a context
 * switch, role change, AAL upgrade, or permission/entitlement version bump
 * automatically invalidates stale protected data. The root `['experience']`
 * prefix is also cleared atomically on every authorization transition (see
 * session-provider clearAllProtectedCache / switchContext / exitDelegation).
 */
export interface ExperienceFingerprint {
  readonly membershipId?: string;
  readonly organizationId?: string;
  readonly branchId?: string;
  readonly clientAccountId?: string;
  readonly portfolioAccountId?: string;
  readonly persona?: string;
  readonly scopeKind?: string;
  readonly aal?: string;
  readonly entitlementsVersion?: number;
  readonly delegated?: boolean;
  readonly delegationId?: string;
}

function fingerprintParts(fp: ExperienceFingerprint | undefined): readonly unknown[] {
  if (!fp) return ['_none_'];
  return [
    fp.membershipId ?? '_none_',
    fp.organizationId ?? '_none_',
    fp.branchId ?? '_none_',
    fp.clientAccountId ?? '_none_',
    fp.portfolioAccountId ?? '_none_',
    fp.persona ?? '_',
    fp.scopeKind ?? '_',
    fp.aal ?? '_',
    fp.entitlementsVersion ?? 0,
    fp.delegated ? 'del' : 'direct',
    fp.delegationId ?? '_none_',
  ];
}

export const experienceQueryKeys = {
  all: ['experience'] as const,
  shell: (fp?: ExperienceFingerprint) =>
    [...experienceQueryKeys.all, ...fingerprintParts(fp), 'shell'] as const,
  routes: (fp?: ExperienceFingerprint) =>
    [...experienceQueryKeys.all, ...fingerprintParts(fp), 'routes'] as const,
  resource: (resourceId: string, fp?: ExperienceFingerprint) =>
    [...experienceQueryKeys.all, ...fingerprintParts(fp), 'resource', resourceId] as const,
  table: (resourceId: string, fp?: ExperienceFingerprint) =>
    [...experienceQueryKeys.resource(resourceId, fp), 'table'] as const,
  form: (resourceId: string, mode: FormMode, fp?: ExperienceFingerprint) =>
    [...experienceQueryKeys.resource(resourceId, fp), 'form', mode] as const,
  data: (resourceId: string, query: GenericResourceListQuery, fp?: ExperienceFingerprint) =>
    [...experienceQueryKeys.resource(resourceId, fp), 'data', query] as const,
  record: (resourceId: string, id: string, fp?: ExperienceFingerprint) =>
    [...experienceQueryKeys.resource(resourceId, fp), 'record', id] as const,
};

export function fetchExperienceShell(signal?: AbortSignal): Promise<ResolvedExperienceShell> {
  return getExperienceJson('/api/experience/shell', signal);
}

export function fetchExperienceRoutes(
  signal?: AbortSignal
): Promise<ResolvedRouteExperienceDescriptor[]> {
  return getExperienceJson('/api/experience/routes', signal);
}

export function fetchResolvedResource(
  resourceId: string,
  signal?: AbortSignal
): Promise<ResolvedResourceExperienceDescriptor> {
  return getExperienceJson(`/api/experience/resources/${encodeURIComponent(resourceId)}`, signal);
}

export function fetchResolvedTable(
  resourceId: string,
  signal?: AbortSignal
): Promise<TableDescriptor> {
  return getExperienceJson(
    `/api/experience/resources/${encodeURIComponent(resourceId)}/table`,
    signal
  );
}

export function fetchResolvedForm(
  resourceId: string,
  mode: FormMode,
  signal?: AbortSignal
): Promise<FormDescriptor> {
  return getExperienceJson(
    `/api/experience/resources/${encodeURIComponent(resourceId)}/form/${encodeURIComponent(mode)}`,
    signal
  );
}

export async function listResolvedResource<TItem extends Record<string, unknown>>(
  binding: ResolvedApiBinding,
  query: GenericResourceListQuery = {},
  signal?: AbortSignal
): Promise<GenericResourceListResponse<TItem>> {
  const search = new URLSearchParams();
  if (query.page !== undefined) search.set('page', String(query.page));
  if (query.pageSize !== undefined) search.set('pageSize', String(query.pageSize));
  if (query.sortField) search.set('sortField', query.sortField);
  if (query.sortDirection) search.set('sortDirection', query.sortDirection);
  if (query.search) search.set('search', query.search);
  if (query.filters && query.filters.length > 0) {
    search.set('filters', JSON.stringify(query.filters));
  }
  if (query.grouping && query.grouping.length > 0) {
    search.set('grouping', JSON.stringify(query.grouping));
  }
  if (query.aggregations && query.aggregations.length > 0) {
    search.set('aggregations', JSON.stringify(query.aggregations));
  }
  const suffix = search.size > 0 ? `?${search.toString()}` : '';
  const raw = await parseJson<unknown>(
    await httpClient(`${resolveBindingPath(binding)}${suffix}`, {
      method: binding.method,
      signal,
    })
  );

  if (Array.isArray(raw)) return { items: raw as TItem[] };
  if (raw && typeof raw === 'object') {
    const record = raw as Record<string, unknown>;
    const items = Array.isArray(record.items)
      ? record.items
      : Array.isArray(record.data)
        ? record.data
        : [];
    return {
      items: items as TItem[],
      pageInfo: record.pageInfo as GenericResourceListResponse<TItem>['pageInfo'],
      summary: record.summary as GenericResourceListResponse<TItem>['summary'],
      aggregates: record.aggregates as GenericResourceListResponse<TItem>['aggregates'],
      groupMetadata: record.groupMetadata as GenericResourceListResponse<TItem>['groupMetadata'],
      availableActions:
        record.availableActions as GenericResourceListResponse<TItem>['availableActions'],
    };
  }
  throw new Error(`Resource list binding ${binding.operationId} returned an invalid response`);
}

export function getResolvedResourceRecord<TRecord extends Record<string, unknown>>(
  binding: ResolvedApiBinding,
  id: string,
  mode: 'view' | 'edit',
  signal?: AbortSignal
): Promise<TRecord> {
  const path = resolveBindingPath(binding, { id });
  return getExperienceJson(`${path}?mode=${encodeURIComponent(mode)}`, signal);
}

export function createResolvedResource<TRecord extends Record<string, unknown>>(
  binding: ResolvedApiBinding,
  values: Record<string, unknown>,
  signal?: AbortSignal
): Promise<TRecord> {
  const idempotencyKey = mutationIdempotencyKey(binding);
  return httpClient(resolveBindingPath(binding), {
    method: binding.method,
    headers: mutationHeaders(idempotencyKey),
    body: JSON.stringify(values),
    signal,
  }).then(parseJson<TRecord>);
}

export function updateResolvedResource<TRecord extends Record<string, unknown>>(
  binding: ResolvedApiBinding,
  id: string,
  values: Record<string, unknown>,
  signal?: AbortSignal
): Promise<TRecord> {
  const idempotencyKey = mutationIdempotencyKey(binding);
  return httpClient(resolveBindingPath(binding, { id }), {
    method: binding.method,
    headers: mutationHeaders(idempotencyKey),
    body: JSON.stringify(values),
    signal,
  }).then(parseJson<TRecord>);
}

export async function deleteResolvedResource(
  binding: ResolvedApiBinding,
  id: string,
  signal?: AbortSignal
): Promise<void> {
  const idempotencyKey = mutationIdempotencyKey(binding);
  await parseJson<unknown>(
    await httpClient(resolveBindingPath(binding, { id }), {
      method: binding.method,
      headers: mutationHeaders(idempotencyKey),
      signal,
    })
  );
}

export async function executeResolvedResourceAction<TResponse = unknown>(
  resourceId: string,
  actionId: string,
  payload: Record<string, unknown>,
  options: { readonly bulk?: boolean; readonly signal?: AbortSignal } = {}
): Promise<TResponse> {
  const actionSegment = options.bulk ? 'bulk-actions' : 'actions';
  const idempotencyKey = crypto.randomUUID();
  const response = await httpClient(
    `/api/resources/${encodeURIComponent(resourceId)}/${actionSegment}/${encodeURIComponent(actionId)}`,
    {
      method: 'POST',
      headers: mutationHeaders(idempotencyKey),
      body: JSON.stringify(payload),
      signal: options.signal,
    }
  );
  return parseJson<TResponse>(response);
}

export async function submitResolvedResourceForm(
  resolved: ResolvedResourceExperienceDescriptor,
  mode: 'create' | 'edit',
  values: Record<string, unknown>,
  id?: string
): Promise<FormSubmissionResult> {
  try {
    const binding = mode === 'create' ? resolved.bindings.create : resolved.bindings.update;
    if (!binding)
      throw new Error(`Resource ${resolved.resource.id} has no authorized ${mode} binding`);
    const payload = resolved.resource.id === 'import-jobs'
      ? await prepareImportPayload(values)
      : values;
    const returnedValues =
      mode === 'create'
        ? await createResolvedResource<Record<string, unknown>>(binding, payload)
        : await updateResolvedResource<Record<string, unknown>>(binding, id ?? '', payload);
    return { status: 'succeeded', returnedValues };
  } catch (error) {
    const apiError = error instanceof ApiError ? error : undefined;
    const details = apiError?.details;
    const fieldErrors = Object.fromEntries(
      (details?.fieldErrors ?? []).map((field) => [
        field.path,
        {
          code: (field.messageKey ?? field.code ?? 'common.error.unexpected') as never,
          ...(field.messageParams ? { params: field.messageParams } : {}),
          correlationId: apiError?.correlationId,
          retryable: details?.retryable,
        },
      ])
    );
    const conflictParams = details?.currentVersion ? { currentVersion: details.currentVersion } : undefined;
    return {
      status: 'failed',
      ...(apiError?.status === 409 ? { conflict: { ...(details?.currentVersion ? { currentVersion: details.currentVersion } : {}) } } : {}),
      ...(Object.keys(fieldErrors).length > 0 ? { fieldErrors } : {}),
      globalErrors: [
        {
          code: (apiError?.key ?? apiError?.code ?? 'common.error.unexpected') as never,
          ...(conflictParams ? { params: conflictParams } : {}),
          correlationId: apiError?.correlationId,
          retryable: details?.retryable,
        },
      ],
    };
  }
}
